import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CookieIcon as Cookie, XIcon as X } from "@phosphor-icons/react";
import { NoiseOverlay, Indicium, Icon } from "@/components/luxury";
import { useAuth } from "@/contexts/AuthContext";

/**
 * GDPR / CCPA cookie consent banner.
 *
 * Shows a non-blocking bottom banner the first time a visitor lands on
 * any page. Persists the choice in localStorage as `cookie_consent` =
 * "all" | "essential". Re-mountable: clearing localStorage shows it again.
 *
 * The choice gates analytics + non-essential cookies via the helpers in
 * `lib/analytics.js`. Essential cookies (auth, session, billing
 * persona) are always allowed because they're required for the service.
 */
const STORAGE_KEY = "cookie_consent";

export function getConsent() {
  try {
    return localStorage.getItem(STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

export function hasAnalyticsConsent() {
  return getConsent() === "all";
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const { user } = useAuth();

  // Native app: cookie banners are a web-browsing artifact — Apple 4.2
  // reviewers read them as "this is a website in a wrapper". The native
  // shell runs essential-cookies-only (no analytics), so consent is
  // implicit and the banner never renders.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.Capacitor?.isNativePlatform?.()) {
      try {
        if (!localStorage.getItem(STORAGE_KEY)) localStorage.setItem(STORAGE_KEY, "essential");
      } catch { /* private mode */ }
    }
  }, []);

  useEffect(() => {
    // Marketing reels are pure recording surfaces — never show the
    // banner there or it ends up in the captured frames.
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/marketing/reel")) {
      return;
    }
    if (typeof window !== "undefined" && window.Capacitor?.isNativePlatform?.()) {
      return; // native shell never shows the banner
    }
    // Admins run the place — don't nag the owner. Default them to
    // essential-only silently; the footer "Cookie settings" link still
    // lets them opt into analytics any time.
    if (user?.role === "admin") {
      try {
        if (!localStorage.getItem(STORAGE_KEY)) localStorage.setItem(STORAGE_KEY, "essential");
      } catch { /* private mode */ }
      const reopenAdmin = () => setVisible(true);
      window.addEventListener("open-cookie-settings", reopenAdmin);
      return () => window.removeEventListener("open-cookie-settings", reopenAdmin);
    }
    if (!getConsent()) setVisible(true);
    // GDPR: withdrawing consent must be as easy as giving it — the
    // "Cookie settings" footer link re-opens this banner at any time.
    const reopen = () => setVisible(true);
    window.addEventListener("open-cookie-settings", reopen);
    return () => window.removeEventListener("open-cookie-settings", reopen);
  }, [user]);

  const choose = async (value) => {
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      /* private mode — banner just dismisses for the session */
    }
    setVisible(false);
    // Notify the rest of the app so analytics can boot if accepted.
    window.dispatchEvent(new CustomEvent("cookie-consent-changed", { detail: value }));

    // Record on the server for audit trail (GDPR / CCPA). We tag the
    // request with a stable anonymous id so a choice made before signup
    // can later be linked to the user's account. Fire-and-forget —
    // never block the UI on the audit call.
    try {
      let anonId = localStorage.getItem("anon_id");
      if (!anonId) {
        anonId = (typeof crypto !== "undefined" && crypto.randomUUID)
          ? crypto.randomUUID()
          : `anon-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        localStorage.setItem("anon_id", anonId);
      }
      const { default: api } = await import("@/lib/api");
      api.post("/consents/record", {
        kind: "cookies",
        value,
        anon_id: anonId,
        origin: "banner",
      }).catch(() => {});
    } catch {
      // Never propagate — the user already saved their choice client-side.
    }
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie preferences"
      data-testid="cookie-consent-banner"
      className="fixed inset-x-3 bottom-3 sm:inset-x-auto sm:bottom-5 sm:right-5 sm:max-w-md z-[60] rounded-2xl bg-ink text-white shadow-luxury-dark border border-white/10 p-5 animate-in fade-in slide-in-from-bottom-4 duration-300 overflow-hidden"
    >
      {/* Subtle grain overlay so the banner reads as paper, not as flat dark UI. */}
      <NoiseOverlay opacity={0.05} />
      <div className="relative flex items-start gap-4">
        <Indicium icon={Cookie} tone="gold" size="md" variant="dark" />
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.25em] text-gold-light font-bold">
            Privacy
          </p>
          <p className="font-spectral text-lg tracking-tight mt-0.5">Cookies &amp; your data</p>
          <div className="gold-hairline mt-3 mb-3 opacity-50" />
          <p className="text-[12px] leading-relaxed text-white/70">
            Essential cookies keep you signed in and process payments. With your
            permission we also use analytics cookies to understand how the
            registry is used and improve it. Read our{" "}
            <Link to="/privacy" className="text-gold-light underline underline-offset-4 hover:text-white">
              Privacy Policy
            </Link>
            .
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-4">
            <Button
              size="sm"
              data-testid="cookie-accept-all"
              onClick={() => choose("all")}
              className="bg-gold-gradient hover:opacity-95 text-ink font-semibold rounded-full h-11 px-5 border-0"
            >
              Accept all
            </Button>
            {/* GDPR/CNIL: refusing must be as easy + as prominent as
                accepting — equal size, weight, and visual presence. */}
            <Button
              size="sm"
              data-testid="cookie-essential-only"
              onClick={() => choose("essential")}
              className="rounded-full h-11 px-5 bg-white hover:bg-white/90 text-ink font-semibold border-0"
            >
              Reject non-essential
            </Button>
          </div>
        </div>
        <button
          type="button"
          aria-label="Dismiss"
          data-testid="cookie-banner-close"
          onClick={() => choose("essential")}
          className="text-white/40 hover:text-white -mt-1 -mr-1 p-3 min-h-[44px] min-w-[44px] flex items-center justify-center transition-colors"
        >
          <Icon as={X} tone="current" size="sm" weight="bold" />
        </button>
      </div>
    </div>
  );
}
